'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { TextField } from './text-field';
import { PICKER_BOX_ATTR, pickerBox, sendThroughCarrier, showSelected, usePopupBelow } from './picker-popup';
const pad = (n) => String(n).padStart(2, '0');
/** 'yyyy-mm-dd' of the local calendar day (toISOString would give the UTC day, a day early in India before 05:30) */
export function toDateFieldValue(value) {
    if (!value)
        return '';
    if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value))
            return value;
        const d = new Date(value);
        return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
    return isNaN(value.getTime()) ? '' : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}
/** 'yyyy-mm-dd' → 'dd-mm-yyyy' */
const toDisplay = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : '');
const toLocalDate = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(0);
    date.setFullYear(y, m - 1, d);
    date.setHours(0, 0, 0, 0);
    return date;
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const POPUP_WIDTH = 300;
/**
 * The dd-mm-yyyy box + calendar logic, shared by DateField and AdaptiveField (type="date")
 * so every date box behaves the same. Spread inputProps on a text <input>, put `calendar` at
 * the left of it (both inside one `relative` box), and show `invalidMessage` as the field error.
 *
 * The calendar is our own popup (month grid, ‹ ›, month / year pick, Clear / Today), opened
 * under the box in a portal so a modal's scroll area does not cut it off.
 */
export function useDateTextInput({ value, onValueChange, minIso = '', maxIso = '', disabled, onBlur, }) {
    const iso = toDateFieldValue(value);
    const hiddenRef = React.useRef(null);
    const popupRef = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [view, setView] = React.useState('days');
    const [shown, setShown] = React.useState({ year: 0, month: 0 });
    const inRange = (d) => (!minIso || d >= minIso) && (!maxIso || d <= maxIso);
    const todayIso = toDateFieldValue(new Date());
    const box = () => pickerBox(hiddenRef.current);
    const handlePick = (e) => {
        const picked = e.target.value;
        if (picked && !inRange(picked))
            return;
        if (picked !== iso)
            onValueChange(picked);
    };
    const pick = (next) => {
        setOpen(false);
        sendThroughCarrier(hiddenRef.current, next, onValueChange);
    };
    const openCalendar = () => {
        if (disabled)
            return;
        if (open)
            return setOpen(false);
        const start = iso || (maxIso && todayIso > maxIso ? maxIso : minIso && todayIso < minIso ? minIso : todayIso);
        setShown({ year: Number(start.slice(0, 4)), month: Number(start.slice(5, 7)) - 1 });
        setView('days');
        setOpen(true);
    };
    // As wide as the box (like the form's other fields), within 300–420px
    const popupStyle = usePopupBelow({ open, setOpen, getBox: box, popupRef, width: (w) => Math.min(Math.max(w, POPUP_WIDTH), 420) });
    // The year list opens at the shown year; free scrolling after that
    React.useLayoutEffect(() => { if (open && view === 'years')
        showSelected(popupRef.current); }, [open, view]);
    const moveMonth = (step) => setShown(({ year, month }) => {
        const d = new Date(year, month + step, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const dayIso = (day) => `${shown.year}-${pad(shown.month + 1)}-${pad(day)}`;
    const firstYear = minIso ? Number(minIso.slice(0, 4)) : new Date().getFullYear() - 100;
    const lastYear = maxIso ? Number(maxIso.slice(0, 4)) : new Date().getFullYear() + 10;
    const cell = (selected, current, off) => cn('rounded-md text-sm transition-colors', selected && 'bg-primary-600 text-white font-semibold', !selected && current && 'text-primary-700 dark:text-primary-300 font-semibold ring-1 ring-inset ring-primary-300 dark:ring-primary-700', !selected && !off && 'text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700', off && 'text-gray-300 dark:text-gray-600 cursor-not-allowed');
    const renderDays = () => {
        const blanks = new Date(shown.year, shown.month, 1).getDay();
        const count = new Date(shown.year, shown.month + 1, 0).getDate();
        return (_jsxs("div", { className: "px-3 pt-3", children: [_jsx("div", { className: "grid grid-cols-7 mb-1", children: WEEK_DAYS.map((d, i) => _jsx("div", { className: "h-8 flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400", children: d }, i)) }), _jsxs("div", { className: "grid grid-cols-7 gap-y-1", children: [Array.from({ length: blanks }, (_, i) => _jsx("div", {}, `b${i}`)), Array.from({ length: count }, (_, i) => {
                            const d = dayIso(i + 1);
                            const off = !inRange(d);
                            return (_jsx("div", { className: "flex justify-center", children: _jsx("button", { type: "button", disabled: off, onClick: () => pick(d), className: cn('w-9 h-9', cell(d === iso, d === todayIso, off)), children: i + 1 }) }, d));
                        })] })] }));
    };
    const renderMonths = () => (_jsx("div", { className: "grid grid-cols-3 gap-2 p-3", children: MONTHS.map((m, i) => {
            const first = `${shown.year}-${pad(i + 1)}-01`, last = `${shown.year}-${pad(i + 1)}-31`;
            const off = (!!maxIso && first > maxIso) || (!!minIso && last < minIso);
            return (_jsx("button", { type: "button", disabled: off, onClick: () => { setShown((s) => ({ ...s, month: i })); setView('days'); }, className: cn('py-3', cell(iso.slice(0, 7) === first.slice(0, 7), todayIso.slice(0, 7) === first.slice(0, 7), off)), children: m }, m));
        }) }));
    const renderYears = () => (_jsx("div", { className: "relative grid grid-cols-4 gap-2 p-3 max-h-64 overflow-y-auto", style: { scrollbarWidth: 'thin' }, children: Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i).map((y) => (_jsx("button", { type: "button", onClick: () => { setShown((s) => ({ ...s, year: y })); setView('months'); }, "data-selected": y === shown.year || undefined, className: cn('py-2', cell(iso.slice(0, 4) === String(y), todayIso.slice(0, 4) === String(y), false)), children: y }, y))) }));
    const step = (dir) => {
        if (view === 'months')
            setShown((s) => ({ ...s, year: s.year + dir }));
        else if (view === 'days')
            moveMonth(dir);
    };
    const arrow = cn('p-1.5 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700', view === 'years' && 'invisible');
    const popup = open && typeof document !== 'undefined' && createPortal(_jsxs("div", { ref: popupRef, role: "dialog", "aria-label": "Choose date", 
        // Keep the focus in the date box (keys, Tab and Esc work from there)
        onMouseDown: (e) => e.preventDefault(), className: "fixed z-[100] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg", style: popupStyle, children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-700", children: [_jsx("button", { type: "button", "aria-label": "Previous", onClick: () => step(-1), className: arrow, children: _jsx(ChevronLeft, { className: "h-4 w-4" }) }), _jsx("button", { type: "button", onClick: () => setView(view === 'days' ? 'months' : view === 'months' ? 'years' : 'days'), className: "px-2 py-1 rounded-md text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700", children: view === 'days' ? `${MONTHS[shown.month]} ${shown.year}` : view === 'months' ? shown.year : 'Pick a year' }), _jsx("button", { type: "button", "aria-label": "Next", onClick: () => step(1), className: arrow, children: _jsx(ChevronRight, { className: "h-4 w-4" }) })] }), view === 'days' ? renderDays() : view === 'months' ? renderMonths() : renderYears(), _jsxs("div", { className: "flex items-center justify-between mx-3 mt-2 py-2.5 border-t border-gray-200 dark:border-gray-700", children: [_jsx("button", { type: "button", onClick: () => pick(''), className: "text-sm text-primary-600 dark:text-primary-400 hover:underline", children: "Clear" }), _jsx("button", { type: "button", disabled: !inRange(todayIso), onClick: () => pick(todayIso), className: "text-sm text-primary-600 dark:text-primary-400 hover:underline disabled:text-gray-300 disabled:no-underline dark:disabled:text-gray-600", children: "Today" })] })] }), document.body);
    const calendar = (_jsxs("span", { className: "flex items-center pointer-events-auto", children: [_jsx("button", { type: "button", tabIndex: -1, "aria-label": "Open calendar", disabled: disabled, onMouseDown: (e) => e.preventDefault(), onClick: () => { box()?.focus(); openCalendar(); }, className: "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:cursor-not-allowed", children: _jsx(Calendar, { className: "h-4 w-4" }) }), _jsx("input", { ref: hiddenRef, type: "date", "data-picker-carrier": true, tabIndex: -1, "aria-hidden": "true", className: "hidden", value: iso, onChange: handlePick }), popup] }));
    return {
        inputProps: {
            value: toDisplay(iso),
            readOnly: true,
            [PICKER_BOX_ATTR]: '',
            onClick: openCalendar,
            onBlur: () => { setOpen(false); onBlur?.(); },
            autoComplete: 'off',
            'aria-haspopup': 'dialog',
            'aria-expanded': open,
            style: disabled ? undefined : { cursor: 'pointer' },
            // Space / ↓ opens the calendar, Esc / Enter close it (not the modal), Backspace / Delete clears;
            // with it closed Enter / Tab keep their form job (next field)
            onKeyDown: (e) => {
                if (disabled)
                    return;
                if ((e.key === 'Escape' || e.key === 'Enter') && open) {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(false);
                }
                else if (e.key === ' ' || (e.key === 'ArrowDown' && !open)) {
                    e.preventDefault();
                    openCalendar();
                }
                else if ((e.key === 'Backspace' || e.key === 'Delete') && iso) {
                    e.preventDefault();
                    pick('');
                }
            },
        },
        invalidMessage: undefined,
        calendar,
    };
}
export function DateField({ label, error, helperText, required, className, value, onChange, onValueChange, minDate, maxDate, disabled, placeholder = 'dd-mm-yyyy', id, name, min, max, inputClassName, size = 'md', title, 'aria-label': ariaLabel, autoFocus, onBlur, }) {
    const date = useDateTextInput({
        value,
        onValueChange: (iso) => {
            onValueChange?.(iso);
            onChange?.(iso ? toLocalDate(iso) : null);
        },
        minIso: minDate ? toDateFieldValue(minDate) : toDateFieldValue(min),
        maxIso: maxDate ? toDateFieldValue(maxDate) : toDateFieldValue(max),
        disabled,
        onBlur,
    });
    return (_jsx("div", { className: className, children: _jsx(TextField, { id: id, name: name, label: label, error: error || date.invalidMessage, helperText: helperText, required: required, disabled: disabled, className: inputClassName, size: size, title: title, "aria-label": ariaLabel, autoFocus: autoFocus, placeholder: placeholder, ...date.inputProps, startAdornment: date.calendar }) }));
}
